import { useQuery } from '@tanstack/react-query'
import { LumpsumRecommendationActionType } from '../../../api/common.types'
import { fetchLumpsumAllocationPlanRecommendation } from '../../api/repository'
import { LUMPSUM_PLANS_API_RETRY_COUNT, isLumpsumQueryFetchFailed, isLumpsumQueryLoading } from '../../../api/queryOptions'
import type {
  LumpsumAllocationPlanRecommendationApiResponse,
  LumpsumAllocationSellPlanRecommendationDetails
} from '../../api/allocationRecommendation.types'
import type { FundMissedGainsInfoParams } from '../../shared/missed-gains/FundMissedGainsInfoBottomSheet'
import { AllocationSellPlanRecommendationSheet } from './AllocationSellPlanRecommendationSheet'

const ALLOCATION_SELL_PLAN_RECOMMENDATION_QUERY_KEY = 'lumpsum-plans-v4-allocation-sell-recommendation'

export function AllocationSellPlanRecommendationContainer(input: {
  onDismiss: () => void
  isin: string
  onMissedGainsInfoClick: (params: FundMissedGainsInfoParams) => void
  transactionCta: unknown
}) {
  const { onDismiss, isin, onMissedGainsInfoClick, transactionCta } = input
  const { planDetails, isLoading, isFetchFailed, onRetry } = useAllocationSellPlanRecommendation(isin, true)

  return (
    <AllocationSellPlanRecommendationSheet
      onDismiss={onDismiss}
      isin={isin}
      planDetails={planDetails}
      isLoading={isLoading}
      isFetchFailed={isFetchFailed}
      onRetry={onRetry}
      onMissedGainsInfoClick={onMissedGainsInfoClick}
      transactionCta={transactionCta}
    />
  )
}

function useAllocationSellPlanRecommendation(isin: string, enabled: boolean): {
  planDetails: LumpsumAllocationSellPlanRecommendationDetails | null
  isLoading: boolean
  isFetchFailed: boolean
  onRetry: () => void
} {
  const query = useQuery({
    queryKey: [ALLOCATION_SELL_PLAN_RECOMMENDATION_QUERY_KEY, isin],
    queryFn: () =>
      fetchLumpsumAllocationPlanRecommendation(isin, LumpsumRecommendationActionType.SELL),
    enabled: enabled && isin.length > 0,
    retry: LUMPSUM_PLANS_API_RETRY_COUNT
  })
  const response = query.data ?? null

  return {
    planDetails: isSellPlanRecommendationDetails(response) ? response : null,
    isLoading: isLumpsumQueryLoading(query),
    isFetchFailed: isLumpsumQueryFetchFailed(query),
    onRetry: () => {
      void query.refetch()
    }
  }
}

function isSellPlanRecommendationDetails(
  response: LumpsumAllocationPlanRecommendationApiResponse | null
): response is LumpsumAllocationSellPlanRecommendationDetails {
  return response != null && 'fundsToBeBought' in response
}
